import { Directive, ElementRef, Input, OnDestroy, inject } from '@angular/core';
import { TooltipService } from '../services/tooltip.service.js';
import { ViewportService } from '../services/viewport.service.js';

@Directive({
  selector: '[appTooltip]',
  standalone: true,
  host: {
    '(mouseenter)': 'onShow()',
    '(mouseleave)': 'onHide()',
    '(focus)': 'onShow()',
    '(blur)': 'onHide()',
    '(click)': 'onClick()',
  },
})
export class TooltipDirective implements OnDestroy {
  private _text: string | null | undefined = '';

  /**
   * Tooltip text. Setting this also mirrors the value into the host's
   * aria-label so icon-only buttons get a screen-reader-accessible name
   * for free (unless the host already has aria-label/aria-labelledby
   * declared explicitly — those win).
   */
  @Input('appTooltip')
  set appTooltipText(value: string | null | undefined) {
    this._text = value;
    this.applyAriaLabel();
  }

  @Input() appTooltipPosition: 'top' | 'bottom' = 'top';

  private tooltip = inject(TooltipService);
  private viewport = inject(ViewportService);
  private host = inject(ElementRef<HTMLElement>);
  private showTimer: any = null;
  private didSetAriaLabel = false;

  // Hover delay matches OS-native tooltips while staying snappier than browser default.
  private static readonly SHOW_DELAY_MS = 350;

  onShow(): void {
    const text = (this._text || '').toString().trim();
    if (!text) return;
    if (this.showTimer) clearTimeout(this.showTimer);
    this.showTimer = setTimeout(() => {
      this.tooltip.show(text, this.host.nativeElement, this.appTooltipPosition);
    }, TooltipDirective.SHOW_DELAY_MS);
  }

  onHide(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    this.tooltip.hide();
  }

  // On a fine pointer (mouse), click-to-hide is unchanged from before — a
  // click always dismisses, matching the desktop mouseenter/mouseleave flow.
  // On a coarse pointer (touch), mouseenter/mouseleave are unreliable signals
  // for "hover" (tap synthesizes them inconsistently across browsers), so tap
  // instead toggles the tooltip directly: show immediately (skip the 350ms
  // hover delay — there's no hover to debounce) or hide if already shown for
  // THIS anchor. Clearing showTimer defensively covers a synthetic mouseenter
  // having queued a delayed show moments earlier in the same tap gesture.
  onClick(): void {
    if (!this.viewport.prefersCoarsePointer) {
      this.onHide();
      return;
    }
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.tooltip.isShownFor(this.host.nativeElement)) {
      this.tooltip.hide();
      return;
    }
    const text = (this._text || '').toString().trim();
    if (!text) return;
    this.tooltip.show(text, this.host.nativeElement, this.appTooltipPosition);
  }

  ngOnDestroy(): void {
    this.onHide();
  }

  private applyAriaLabel(): void {
    const host = this.host.nativeElement;
    // Respect an explicit aria-labelledby (always) or a manually-set aria-label
    // that we didn't put there ourselves.
    if (host.hasAttribute('aria-labelledby')) return;
    if (host.hasAttribute('aria-label') && !this.didSetAriaLabel) return;

    const text = (this._text || '').toString().trim();
    if (text) {
      host.setAttribute('aria-label', text);
      this.didSetAriaLabel = true;
    } else if (this.didSetAriaLabel) {
      host.removeAttribute('aria-label');
      this.didSetAriaLabel = false;
    }
  }
}
